import * as React from "react";
import {
  List,
  DataTable,
  BooleanField,
  SearchInput,
  BooleanInput,
  EditButton,
  DateField,
  FunctionField,
  ReferenceInput,
  SelectInput,
  SelectArrayInput,
  ReferenceArrayInput,
  DateTimeInput,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  AutocompleteArrayInput,
  AutocompleteInput,
  useListContext,
  ArrayField,
  useDataProvider,
  useNotify,
  useRefresh,
  LinearProgress,
  Button,
  Confirm,
  usePermissions,
} from "react-admin";
import { shortWeekdayToLong } from "../utils/DateTimeUtils";
import { getCountries } from "../enums/Countries";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { validateRequired } from "../utils/Validations";
import {
  ENTITY_AVAILABILITY_STATUS_NAME,
  ENTITY_AVAILABILITY_STATUS_OPTIONS,
} from "../utils/EntityAvailabilityStatusUtils";
import { Archive, Check, Close } from "@mui/icons-material";
import { ROLE_TYPES } from "../enums/Roles";

const STATUS_CONFIG = {
  [ENTITY_AVAILABILITY_STATUS.ACTIVE]: {
    icon: <Check />,
    errorMessage: "Ha ocurrido un error al activar los banners",
    actionLabel: "Activar",
    confirmTitle: "Activar",
    confirmContent: "¿Estás seguro de activar los banners seleccionados?",
    loadingMessage: "Activando banners...",
  },
  [ENTITY_AVAILABILITY_STATUS.INACTIVE]: {
    icon: <Close />,
    errorMessage: "Ha ocurrido un error al desactivar los banners",
    actionLabel: "Desactivar",
    confirmTitle: "Desactivar",
    confirmContent: "¿Estás seguro de desactivar los banners seleccionados?",
    loadingMessage: "Desactivando banners...",
  },
  [ENTITY_AVAILABILITY_STATUS.ARCHIVED]: {
    icon: <Archive />,
    errorMessage: "Ha ocurrido un error al archivar los banners",
    actionLabel: "Archivar",
    confirmTitle: "Archivar",
    confirmContent: "¿Estás seguro de archivar los banners seleccionados?",
    loadingMessage: "Archivando banners...",
  },
};

const BannerBulkActionButtons = () => {
  // Centralized state management
  const [confirmStates, setConfirmStates] = React.useState({
    [ENTITY_AVAILABILITY_STATUS.ACTIVE]: false,
    [ENTITY_AVAILABILITY_STATUS.INACTIVE]: false,
    [ENTITY_AVAILABILITY_STATUS.ARCHIVED]: false,
  });
  const [isLoading, setIsLoading] = React.useState(false);

  const { selectedIds, onUnselectItems } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // Generic handler for all status changes
  const onBulkStatusClick = (status) => {
    if (!selectedIds?.length) return;
    setConfirmStates((prev) => ({ ...prev, [status]: true }));
  };

  // Generic function to close confirmation dialogs
  const closeConfirmDialog = (status) => {
    setConfirmStates((prev) => ({ ...prev, [status]: false }));
  };

  const handleBulksStatusConfirm = (status) => () => {
    if (!selectedIds?.length) return;
    const { loadingMessage, errorMessage } = STATUS_CONFIG[status];

    notify(loadingMessage);
    setIsLoading(true);

    return dataProvider
      .updateStatus("banners", {
        bannerIds: selectedIds,
        status: status,
      })
      .then(() => {
        notify("Se han actualizado los estados de los banners correctamente.", {
          type: "success",
        });
        onUnselectItems();
        refresh();
      })
      .catch((error) => {
        notify(`${errorMessage}: ${error?.body?.message || error.message}`, {
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
        closeConfirmDialog(status);
      });
  };

  return (
    <>
      {isLoading ? (
        <LinearProgress />
      ) : (
        Object.entries(STATUS_CONFIG).map(([status, config]) => (
          <Button
            key={status}
            label={config.actionLabel}
            onClick={() => onBulkStatusClick(status)}
            disabled={isLoading}
          >
            {config.icon}
          </Button>
        ))
      )}

      {Object.entries(STATUS_CONFIG).map(([status, config]) => (
        <Confirm
          key={status}
          isOpen={confirmStates[status]}
          title={`${config.confirmTitle} ${selectedIds?.length} banners`}
          content={config.confirmContent}
          onConfirm={handleBulksStatusConfirm(status)}
          onClose={() => closeConfirmDialog(status)}
          loading={isLoading}
        />
      ))}
    </>
  );
};

const BannerProductFilter = ({ source, label }) => {
  const { filterValues } = useListContext();
  return (
    <ReferenceInput
      source={source}
      reference="products"
      perPage={100}
      sort={{ field: "name", order: "ASC" }}
      filter={{
        restaurant: filterValues?.restaurantProduct,
        status: [ENTITY_AVAILABILITY_STATUS.ACTIVE],
      }}
      label={label}
    >
      <SelectInput
        optionText="name"
        disabled={!filterValues?.restaurantProduct}
        label={label}
      />
    </ReferenceInput>
  );
};

const BannerFilter = () => [
  <SearchInput source="title" alwaysOn />,
  <SelectArrayInput
    label="Estado"
    source="status"
    choices={ENTITY_AVAILABILITY_STATUS_OPTIONS}
    validate={validateRequired}
    alwaysOn
  />,
  <BooleanInput label="Válidos hoy" source="valid" />,
  <BooleanInput label="Programados" source="scheduled" />,
  <ReferenceArrayInput
    source="restaurants"
    reference="restaurants"
    perPage={100}
    filter={{
      active: true,
      type: RESTAURANT_TYPES.ALL,
    }}
    label="Restaurante(s)"
  >
    <AutocompleteArrayInput
      filterToQuery={(searchText) => ({
        name: searchText,
        type: RESTAURANT_TYPES.ALL,
      })}
      label="Restaurante(s)"
      optionText="name"
    />
  </ReferenceArrayInput>,
  <ReferenceInput
    source="restaurantProduct"
    reference="restaurants"
    perPage={100}
    filter={{ active: true }}
    label="Producto (Restaurante)"
  >
    <AutocompleteInput
      filterToQuery={(searchText) => ({ name: searchText })}
      label="Producto (Restaurante)"
      optionText="name"
    />
  </ReferenceInput>,
  // `products` is the array field on the banner. The previous `product` was a legacy
  // single-value field that only 2 of 26 banners still carry, so the filter matched
  // almost nothing. SectionList filters the same way (source="products").
  <BannerProductFilter source="products" label="Producto" />,
  <DateTimeInput source="startDate" label="Fecha de Inicio" />,
  <DateTimeInput source="endDate" label="Fecha de Fin" />,
  <SelectArrayInput
    optionText="name"
    label="Días recurrentes"
    source="recurrentDays"
    choices={[
      { id: "Mo", name: "Lunes" },
      { id: "Tu", name: "Martes" },
      { id: "We", name: "Miércoles" },
      { id: "Th", name: "Jueves" },
      { id: "Fr", name: "Viernes" },
      { id: "Sa", name: "Sábado" },
      { id: "Su", name: "Domingo" },
    ]}
  />,
  <SelectArrayInput
    label="Países"
    source="countries"
    choices={getCountries()}
  />,
  <BooleanInput label="Visible" source="visible" />,
];

const BannerList = () => {
  const { permissions } = usePermissions();

  return (
    <List
      title="Banners"
      filters={BannerFilter()}
      sort={{ field: "order", order: "ASC" }}
      filterDefaultValues={{
        status: [
          ENTITY_AVAILABILITY_STATUS.ACTIVE,
          ENTITY_AVAILABILITY_STATUS.INACTIVE,
        ],
      }}
    >
      <DataTable
        rowClick="show"
        bulkActionButtons={
          permissions === ROLE_TYPES.ADMIN && <BannerBulkActionButtons />
        }
      >
        <DataTable.Col source="title" label="Nombre" />
        <DataTable.Col source="type" label="Tipo" />
        <DataTable.Col source="products" label="Producto(s)">
          <ReferenceArrayField
            label="Producto(s)"
            reference="products"
            source="products"
          >
            <SingleFieldList linkType={false}>
              <ChipField source="name" size="small" />
            </SingleFieldList>
          </ReferenceArrayField>
        </DataTable.Col>
        <DataTable.Col source="restaurants" label="Restaurante(s)">
          <ReferenceArrayField
            label="Restaurante(s)"
            reference="restaurants"
            source="restaurants"
          >
            <SingleFieldList linkType={false}>
              <ChipField source="name" size="small" />
            </SingleFieldList>
          </ReferenceArrayField>
        </DataTable.Col>
        <DataTable.Col source="videos" label="Video(s)">
          <ReferenceArrayField
            label="Video(s)"
            reference="videos"
            source="videos"
          >
            <SingleFieldList linkType={false}>
              <ChipField source="name" size="small" />
            </SingleFieldList>
          </ReferenceArrayField>
        </DataTable.Col>
        <DataTable.Col source="startDate" label="Fecha de Inicio">
          <DateField
            source="startDate"
            label="Fecha de Inicio"
            showTime={true}
            emptyText="-"
          />
        </DataTable.Col>
        <DataTable.Col source="endDate" label="Fecha de Fin">
          <DateField
            source="endDate"
            label="Fecha de Fin"
            showTime={true}
            emptyText="-"
          />
        </DataTable.Col>
        <DataTable.Col source="recurrentSchedule" label="Días recurrentes">
          <FunctionField
            source="recurrentSchedule"
            render={(record) => {
              const days =
                record?.recurrentSchedule?.flatMap((d) =>
                  d.days.map((day) => shortWeekdayToLong(day))
                ) || [];

              return days?.length ? days.join(", ") : "-";
            }}
            label="Días recurrentes"
          />
        </DataTable.Col>
        <DataTable.Col source="countries" label="Países">
          <ArrayField label="Países" source="countries">
            <SingleFieldList linkType={false}>
              <FunctionField
                render={(record) => (
                  <ChipField
                    record={{ name: record }}
                    source="name"
                    size="small"
                  />
                )}
              />
            </SingleFieldList>
          </ArrayField>
        </DataTable.Col>
        <DataTable.NumberCol source="order" label="Orden" />
        <DataTable.Col source="visible" label="Visible">
          <BooleanField source="visible" label="Visible" />
        </DataTable.Col>
        <DataTable.Col source="featured" label="Destacado">
          <BooleanField source="featured" label="Destacado" />
        </DataTable.Col>
        <DataTable.Col source="status" label="Estado">
          <FunctionField
            source="status"
            label="Estado"
            render={(record) => ENTITY_AVAILABILITY_STATUS_NAME[record.status]}
          />
        </DataTable.Col>
        <DataTable.Col>
          <EditButton />
        </DataTable.Col>
      </DataTable>
    </List>
  );
};

export default BannerList;
